import { FirestoreInstance } from '@griply/services';
import {
  doc as _doc,
  query as _query,
  where as _where,
} from 'firebase/firestore';
import { getErrorReporter } from 'shared/services/error-tracking';

export const doc = _doc as unknown as (
  firestore: FirestoreInstance,
  path: string,
  id: string,
) => ReturnType<typeof _doc>;

const rawQuery = _query as unknown as (
  ref: unknown,
  ...constraints: unknown[]
) => ReturnType<typeof _query>;

const collectionPathOf = (ref: unknown): string =>
  typeof (ref as { path?: unknown })?.path === 'string'
    ? (ref as { path: string }).path
    : 'unknown';

const messageOf = (error: unknown): string =>
  error instanceof Error ? error.message : String(error);

// Sentry's JSON serialization drops raw undefined, so it needs a visible marker
const printable = (value: unknown): unknown =>
  value === undefined
    ? '<undefined>'
    : Array.isArray(value)
      ? value.map((item) => (item === undefined ? '<undefined>' : item))
      : value;

type BufferedFilter = {
  field: string;
  op: string;
  value: unknown;
  hasUndefined: boolean;
};

// query construction is synchronous, so filters buffered by where() always belong to the next query() call
const pendingFilters: BufferedFilter[] = [];

export const where: typeof _where = (fieldPath, opStr, value) => {
  const field = String(fieldPath);
  const hasUndefined =
    value === undefined || (Array.isArray(value) && value.includes(undefined));
  pendingFilters.push({
    field,
    op: opStr,
    value: printable(value),
    hasUndefined,
  });

  try {
    return _where(fieldPath, opStr, value);
  } catch (error: unknown) {
    // eager SDK validation — query() never runs for this build, so report here
    getErrorReporter().captureException(
      new Error(
        `Firestore where('${field}', '${opStr}') construction failed: ${messageOf(error)}`,
      ),
      {
        tags: {
          source: 'firestore-query-build',
          collection: 'unknown',
          field,
          op: opStr,
        },
        extra: { filters: pendingFilters.splice(0), originalError: error },
        fingerprint: ['firestore-query-build', field, opStr],
      },
    );
    throw error;
  }
};

export const query = ((ref: unknown, ...constraints: unknown[]) => {
  const filters = pendingFilters.splice(0);
  const collection = collectionPathOf(ref);

  try {
    const result = rawQuery(ref, ...constraints);

    const undefinedFields = filters
      .filter(({ hasUndefined }) => hasUndefined)
      .map(({ field }) => field);
    if (undefinedFields.length) {
      getErrorReporter().captureException(
        new Error(
          `Firestore query on '${collection}' built with undefined filter value(s): ${undefinedFields.join(', ')}`,
        ),
        {
          tags: { source: 'firestore-undefined-filter', collection },
          extra: { filters },
          fingerprint: [
            'firestore-undefined-filter',
            collection,
            ...undefinedFields,
          ],
        },
      );
    }

    return result;
  } catch (error: unknown) {
    getErrorReporter().captureException(
      new Error(
        `Firestore query build failed on '${collection}': ${messageOf(error)}`,
      ),
      {
        tags: { source: 'firestore-query-build', collection },
        extra: { filters, originalError: error },
        fingerprint: ['firestore-query-build', collection],
      },
    );
    throw error;
  }
}) as typeof _query;
